/**
 * useBarberStatuses — estado en tiempo real (barberStatus/{barberId}) de varios
 * barberos a la vez. Devuelve un Map barberId → BarberStatus (solo los que tienen
 * documento).
 *
 * Nace para la pantalla pública de la TV (issue #19): tenía que mostrar si el
 * barbero está en pausa y cuánto le queda, y no leía barberStatus. La lectura de
 * esa colección es pública (firestore.rules) precisamente para esto.
 */
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { BarberStatus } from '@/types';

export function useBarberStatuses(barberIds: string[]): Map<string, BarberStatus> {
  const [statuses, setStatuses] = useState<Map<string, BarberStatus>>(() => new Map());
  // Clave estable para no resuscribir en cada render por identidad del array.
  const key = [...barberIds].sort().join('|');

  useEffect(() => {
    const ids = key ? key.split('|') : [];
    if (ids.length === 0) {
      setStatuses(new Map());
      return;
    }
    const current = new Map<string, BarberStatus>();
    const unsubs = ids.map((id) =>
      onSnapshot(
        doc(db, 'barberStatus', id),
        (snap) => {
          if (snap.exists()) current.set(id, snap.data() as BarberStatus);
          else current.delete(id);
          setStatuses(new Map(current));
        },
        (err) => {
          // Sin estado se degrada a "sin pausa" en vez de romper la pantalla.
          console.error('Error subscribing to barberStatus:', err);
        }
      )
    );
    return () => unsubs.forEach((u) => u());
  }, [key]);

  return statuses;
}

// La regla de negocio vive en src/lib/breaks.ts, compartida con el resto de
// pantallas. Se reexporta para no cambiar los puntos de importación.
export { pendingBreakMinutes as remainingBreakMinutes, isBreakActive, isBreakScheduled } from '@/lib/breaks';
